import axios from '@/utils/request'
import { AxiosPromise, AxiosRequestConfig } from 'axios'
import { BaseResponse, ResponseStatus, Pagination } from '.'
import { MemberSnippetEntity } from '@/api/member'
import { TournamentEntity } from '@/api/tournament'
import { TournamentTeamEntity } from '@/api/tournamentTeam'

export { BaseResponse, ResponseStatus }

export interface RelatedTournament {
  tournamentId: string
  stageId: string
  name: string
  game: string
  banner?: string
  logo?: string
  groupNumber: number
}

export interface JoinableMatchEntity {
  matchGameId: string
  matchTeamId: string
  relatedTournament: RelatedTournament
  tournamentBanner: string
  tournamentLogo: string
  isCheckedIn: boolean
  isRosterConfirmed: boolean
  role: CheckInRoleType
  captainName: string
}

export enum MatchState {
  Initial = 'Initial',
  InProgress = 'InProgress',
  Completed = 'Completed'
}

export enum LeagueMap {
  SummonersRift = 11,
  HowlingAbyss = 12
}

export enum LeaguePickMode {
  Blind = 1,
  Competitive = 2,
  Random = 4,
  Tournament = 6
}

export interface LeagueLobby {
  customGameId: string
  name: string
  password: string
}

export interface MatchEntity {
  matchId: string
  relatedTournament: RelatedTournament
  currentGameRound: number
  totalGameRound: number
  playersPerTeam: number
  state: MatchState
  latestMatchGameId: string
  isAutoGetGameEvidence: boolean
  leagueMap: LeagueMap | null
  leaguePickMode: LeaguePickMode | null
  leagueLobby: LeagueLobby | null
}

export enum MatchGameState {
  Initial = 'Initial',
  Waiting = 'Waiting',
  Ready = 'Ready',
  Playing = 'Playing',
  ResultSubmitted = 'ResultSubmitted',
  Disputed = 'Disputed',
  Completed = 'Completed'
}

export interface MatchGameEntity {
  matchGameId: string
  matchId: string
  gameRound: number
  state: MatchGameState
  checkIns: CheckInEntity[]
  teams: MatchTeamEntity[]
  readyTeams: string[]
  checkInStartTime: number
  checkInEndTime: number
  draftSessionId: string | null
}

export interface MatchGameListItemWithResultResponse {
  matchGameId: string
  gameRound: number
  checkInStartTime: number
  results: MatchGameResultEntity[]
  state: MatchState
}

export enum CheckInRoleType {
  Captain = 'Captain',
  Member = 'Member'
}

export interface CheckInEntity {
  matchTeamId: string
  member: MemberSnippetEntity
  role: CheckInRoleType
  isCheckedIn: boolean
  gameAccountNickname: string
}

export interface MatchTeamEntity {
  matchTeamId: string
  name: string
  isDisqualified: boolean
  logo: string
}

export enum GameResult {
  Lose = 'Lose',
  Draw = 'Draw',
  Win = 'Win'
}

export interface MatchGameResultEntity {
  matchTeamId: string
  score: number
  result: GameResult
  createdBy: string
  isFinal: boolean
  createTime: number
}

export interface SubmitMatchResultRequest {
  matchTeamId: string
  score: number
  result: GameResult
}

export interface MatchGameEvidenceEntity {
  matchTeamId: string
  file: string
  createTime: number
}

export interface MatchChatMessageEntity {
  chatMessageId: string
  member: MemberSnippetEntity
  matchTeamId: string
  message: string
  createTime: number
}

export enum MatchGameType {
  All = '',
  Lol = 'lol',
  Raingbow6 = 'rainbow6',
  Csgo = 'csgo',
  Valorant = 'valorant',
  WildRift = 'wildRift',
  Aov = 'aov',
  SlamDunk = 'slamDunk',
  Fifa21 = 'fifa21',
  Forts = 'forts'
}

export interface MatchGameRosterEntity {
  matchTeamId: string
  playersPerTeam: number
  players: MatchGameRosterPlayerEntity[]
}

export interface MatchGameRosterPlayerEntity {
  member: MemberSnippetEntity
  role: CheckInRoleType
  gameAccountNickname: string
  isLineup: boolean
}

export interface MemberMatchHistoryEntity {
  match: MatchEntity
  roundOf: number
  startTime: number
  teamResults: MemberMatchHistoryTeamResultEntity[]
}

export interface MemberMatchHistoryTeamResultEntity {
  isMemberTeam: boolean
  matchTeamId: string
  result: string
  teamName: string
  teamLogo: string
  loseCount: number
  winCount: number
}

export interface MemberMatchHistoryPaginatedResponse {
  items: MemberMatchHistoryEntity[]
  hasMorePage: boolean
}

export class MatchAPI {
  public static API_PREFIX = '/match/v1'

  /**
   * Get match
   * @param matchId {string}
   * @returns {AxiosPromise<BaseResponse<MatchEntity>}
   */
  public static getMatch(
    matchId: string
  ): AxiosPromise<BaseResponse<MatchEntity>> {
    return axios.get(
      `${this.API_PREFIX}/matches/${encodeURIComponent(matchId)}`
    )
  }

  /**
   * Get match game
   * @param matchGameId {string}
   * @returns {AxiosPromise<BaseResponse<MatchGameEntity>}
   */
  public static getMatchGame(
    matchGameId: string
  ): AxiosPromise<BaseResponse<MatchGameEntity>> {
    return axios.get(
      `${this.API_PREFIX}/match-games/${encodeURIComponent(matchGameId)}`
    )
  }

  /**
   * Get match game results
   * @param matchGameId {string}
   * @returns {AxiosPromise<BaseResponse<MatchGameResultEntity[]>}
   */
  public static getMatchGameResults(
    matchGameId: string
  ): AxiosPromise<BaseResponse<MatchGameResultEntity[]>> {
    return axios.get(
      `${this.API_PREFIX}/match-games/${encodeURIComponent(
        matchGameId
      )}/results`
    )
  }

  /**
   * Submit match game results
   * @param matchGameId {string}
   * @param request {SubmitMatchResultRequest}
   * @returns {AxiosPromise<BaseResponse>}
   */
  public static submitMatchGameResults(
    matchGameId: string,
    request: SubmitMatchResultRequest[]
  ): AxiosPromise<BaseResponse> {
    return axios.post(
      `${this.API_PREFIX}/match-games/${encodeURIComponent(
        matchGameId
      )}/results`,
      request
    )
  }

  /**
   * Get match game evidence
   * @param matchGameId {string}
   * @returns {AxiosPromise<BaseResponse<MatchGameEvidenceEntity[]>}
   */
  public static getMatchGameEvidence(
    matchGameId: string
  ): AxiosPromise<BaseResponse<MatchGameEvidenceEntity[]>> {
    return axios.get(
      `${this.API_PREFIX}/match-games/${encodeURIComponent(
        matchGameId
      )}/evidence`
    )
  }

  /**
   * Submit match evidence file / image
   * @param matchGameId {string}
   * @param file {File | Blob}
   * @param gameIntegration
   * @param requestConfig {AxiosRequestConfig}
   */
  public static submitMatchGameEvidence(
    matchGameId: string,
    file: File | Blob,
    gameIntegration: boolean = false,
    requestConfig: AxiosRequestConfig | undefined = undefined
  ): AxiosPromise<BaseResponse> {
    const request = new FormData()
    request.append('file', file)
    return axios.post(
      `${this.API_PREFIX}/match-games/${encodeURIComponent(
        matchGameId
      )}/evidence${gameIntegration ? '?gameIntegration=true' : ''}`,
      request,
      requestConfig
    )
  }

  /**
   * Get member joinable match rooms
   * @param memberId {string}
   * @returns {AxiosPromise<BaseResponse<JoinableMatchEntity[]>}
   */
  public static getMemberJoinableMatchRooms(
    memberId: string = '@me'
  ): AxiosPromise<BaseResponse<JoinableMatchEntity[]>> {
    return axios.get(`${this.API_PREFIX}/members/${memberId}/joinable-matches`)
  }

  /**
   * Check in match game
   * @param matchGameId {string}
   * @returns {AxiosPromise<BaseResponse>}
   */
  public static checkIn(matchGameId: string): AxiosPromise<BaseResponse> {
    return axios.post(
      `${this.API_PREFIX}/match-games/${encodeURIComponent(
        matchGameId
      )}/check-in`
    )
  }

  /**
   * Captain ready
   * @param matchGameId {string}
   * @returns {AxiosPromise<BaseResponse>}
   */
  public static captainReady(matchGameId: string): AxiosPromise<BaseResponse> {
    return axios.post(
      `${this.API_PREFIX}/match-games/${encodeURIComponent(matchGameId)}/ready`
    )
  }

  /**
   * Get match chat message history
   * @param matchId {string}
   * @param page {number}
   * @returns {AxiosPromise<BaseResponse>}
   */
  public static getChatMessages(
    matchId: string,
    page: number = 1
  ): AxiosPromise<BaseResponse<Pagination<MatchChatMessageEntity>>> {
    return axios.get(
      `${this.API_PREFIX}/matches/${encodeURIComponent(matchId)}/chat-messages`,
      {
        params: {
          page
        }
      }
    )
  }

  /**
   * Request assistance from tournament organizer (RequestAssistance)
   * @param matchId {string}
   */
  public static requestMatchAssistance(
    matchId: string
  ): AxiosPromise<BaseResponse> {
    return axios.post(
      `${this.API_PREFIX}/matches/${matchId}/request-assistance`
    )
  }

  /**
   * Get match game roster
   * @param matchGameId {string}
   * @returns {AxiosPromise<BaseResponse>}
   */
  public static getMatchGameRoster(
    matchGameId: string
  ): AxiosPromise<BaseResponse<MatchGameRosterEntity>> {
    return axios.get(
      `${this.API_PREFIX}/match-games/${encodeURIComponent(matchGameId)}/roster`
    )
  }

  public static modifyRoster(
    matchGameId: string,
    memberIds: string[]
  ): AxiosPromise<BaseResponse> {
    return axios.post(
      `${this.API_PREFIX}/match-games/${encodeURIComponent(
        matchGameId
      )}/roster`,
      {
        memberIds: memberIds
      }
    )
  }

  /**
   * Get member match history
   * @param memberId Member ID
   * @param page Page number
   * @returns {BaseResponse} API Response
   */
  public static getMemberMatchHistory(
    memberId: string,
    page: number = 1,
    game: string = ''
  ): AxiosPromise<BaseResponse<MemberMatchHistoryPaginatedResponse>> {
    return axios.get(`/match/v1/members/${memberId}/history`, {
      params: {
        page: page,
        gameType: game
      }
    })
  }

  public static updateLeagueLobby(
    matchId: string,
    customGameId: string,
    name: string,
    password: string
  ): AxiosPromise<BaseResponse> {
    return axios.post(
      `/match/v1/matches/${encodeURIComponent(matchId)}/update-league-lobby`,
      {
        customGameId,
        name,
        password
      }
    )
  }

  /**
   * Change match team captain
   * @param matchGameId Match Game ID
   * @param matchTeamId Match Team ID
   * @param newCaptainMemberId New Captain Member ID
   * @returns {BaseResponse} API Response
   */
  public static changeMatchTeamCaptain(
    matchGameId: string,
    matchTeamId: string,
    newCaptainMemberId: string
  ): AxiosPromise<BaseResponse> {
    return axios.post(
      `/match/v1/match-games/${encodeURIComponent(
        matchGameId
      )}/match-teams/${encodeURIComponent(matchTeamId)}/change-captain`,
      {
        newCaptainMemberId: newCaptainMemberId
      }
    )
  }

  /**
   * Get match game by draft session Id
   * @param draftSessionId Draft Session ID
   * @returns {BaseResponse} API Response
   */
  public static getMatchGameByDraftSessionId(
    draftSessionId: string
  ): AxiosPromise<BaseResponse<MatchGameEntity>> {
    return axios.get(
      `/match/v1/match-games/draft-session/${encodeURIComponent(
        draftSessionId
      )}`
    )
  }
}
